import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { getIsThirdPartyCookieDisabled } from '@buy/utils/get-is-thirdparty-cookie-disabled'
import { APP_PATHS, STORAGE_CONST } from '@common/constants'
import { useDispatch } from 'react-redux'
import { buyUserActions } from '@buy/state/user/actions'

export const useRedirectOnLoad = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const [didRedirect, setDidRedirect] = useState<boolean | undefined>(undefined)

    const dispatch = useDispatch()

    useEffect(() => {
        try {
            // If silent auth iframe is returning from the auth server with a code
            const isThirdPartyCookieDisabled = getIsThirdPartyCookieDisabled()
            const isInHiddenSilentAuthIFrame = !!window.sessionStorage.getItem('IsSilentAuth')
            if (!isThirdPartyCookieDisabled && isInHiddenSilentAuthIFrame) {
                // We aren't really redirecting here, but this gets the caller
                // to not render the actual page inside the iframe
                // See src/common/views/RootView/index.tsx
                setDidRedirect(true)

                // The code in src/products/buy/saga/user/get-token.ts will end silent auth from the iframe
                // and pass control back to the parent frame
                dispatch(buyUserActions.getAccessToken())

                // Don't continue any more code in the iframe
                return
            }

            const { pathname, search, hash } = location
            // check if there is a saved route
            const searchParams = new URLSearchParams(search)
            const savedRoute = localStorage.getItem(STORAGE_CONST.SAVED_ROUTE) || pathname
            const savedQuery = localStorage.getItem(STORAGE_CONST.SAVED_QUERY)

            if (savedQuery) {
                new URLSearchParams(savedQuery).forEach((val, key) => {
                    searchParams.set(key, val)
                })
            }

            const combinedQuery = searchParams.toString() ? `?${searchParams.toString()}` : ''
            // check to make sure the route is valid
            const isRouteDefined = Object.values(APP_PATHS).find((path) => savedRoute.includes(path))
            // Once we get the saved Route and Query values, delete the stored Route and Query so that after user don't have to get redirected to the stored page even if it's valid path
            localStorage.removeItem(STORAGE_CONST.SAVED_ROUTE)
            localStorage.removeItem(STORAGE_CONST.SAVED_QUERY)
            // if the route is valid and is different than the current one or the current search value
            if (isRouteDefined && (savedRoute !== pathname || combinedQuery !== search)) {
                // redirect to that route
                setDidRedirect(true)
                navigate({ pathname: savedRoute, search: combinedQuery, hash })
            } else {
                setDidRedirect(false)
            }
        } catch (e: any) {
            console.error(e.message)
        }
    }, [navigate, location, dispatch])

    return {
        didRedirect,
    }
}
